// FieldToolbar — minimal floating pill toolbar
// Docks to any screen edge, shows icons that expand to labels on hover
// Draggable by grabbing the handle

import React, { useState, useRef, useCallback } from 'react';

// ============================================================
// Action definitions
// ============================================================

export interface ToolbarAction {
  id: string;
  icon: string;   // emoji or single char
  label: string;
  shortLabel?: string;
  color: string;
}

const DEFAULT_ACTIONS: ToolbarAction[] = [
  { id: 'new-unit',       icon: '🔷', label: 'New Unit',        color: '#4DC4D2' },
  { id: 'new-chat',       icon: '💬', label: 'New Chat',       color: '#325BA8' },
  { id: 'draft-doc',      icon: '📄', label: 'Draft Document', color: '#4056A6' },
  { id: 'draft-email',    icon: '📧', label: 'Draft Email',    color: '#892987' },
  { id: 'new-task',       icon: '✅', label: 'New Task',        color: '#E94257' },
  { id: 'schedule',       icon: '📅', label: 'Schedule',       color: '#D97706' },
  { id: 'quick-note',     icon: '📝', label: 'Quick Note',     color: '#059669' },
  { id: 'search',         icon: '🔍', label: 'Search',         color: '#6B7280' },
];

// ============================================================
// Edge type & positioning
// ============================================================

type Edge = 'bottom' | 'top' | 'left' | 'right';

interface ToolbarPosition {
  edge: Edge;
  offset: number; // 0-1 along the edge
}

function getToolbarStyle(pos: ToolbarPosition, isVertical: boolean): React.CSSProperties {
  const MARGIN = 16;

  switch (pos.edge) {
    case 'bottom':
      return {
        bottom: MARGIN,
        left: `${pos.offset * 100}%`,
        transform: 'translateX(-50%)',
      };
    case 'top':
      return {
        top: MARGIN,
        left: `${pos.offset * 100}%`,
        transform: 'translateX(-50%)',
      };
    case 'left':
      return {
        left: MARGIN,
        top: `${pos.offset * 100}%`,
        transform: 'translateY(-50%)',
      };
    case 'right':
      return {
        right: MARGIN,
        top: `${pos.offset * 100}%`,
        transform: 'translateY(-50%)',
      };
  }
}

// ============================================================
// Toolbar component
// ============================================================

interface FieldToolbarProps {
  actions?: ToolbarAction[];
  onAction?: (actionId: string) => void;
}

export default function FieldToolbar({ actions = DEFAULT_ACTIONS, onAction }: FieldToolbarProps) {
  const [position, setPosition] = useState<ToolbarPosition>({ edge: 'bottom', offset: 0.5 });
  const [hovered, setHovered] = useState(false);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef<{ startX: number; startY: number; moved: boolean } | null>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);

  const isVertical = position.edge === 'left' || position.edge === 'right';

  // Drag handling — determines which edge to dock to
  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    // Only start drag from the handle area
    const target = e.target as HTMLElement;
    if (!target.dataset.dragHandle) return;

    e.preventDefault();
    e.stopPropagation();
    dragRef.current = { startX: e.clientX, startY: e.clientY, moved: false };
    setIsDragging(true);
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, []);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    if (Math.abs(dx) + Math.abs(dy) > 8) {
      dragRef.current.moved = true;
    }

    if (!dragRef.current.moved) return;

    // Determine closest edge
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const x = e.clientX;
    const y = e.clientY;

    const distTop = y;
    const distBottom = vh - y;
    const distLeft = x;
    const distRight = vw - x;
    const minDist = Math.min(distTop, distBottom, distLeft, distRight);

    let edge: Edge;
    let offset: number;

    if (minDist === distBottom) {
      edge = 'bottom';
      offset = Math.max(0.1, Math.min(0.9, x / vw));
    } else if (minDist === distTop) {
      edge = 'top';
      offset = Math.max(0.1, Math.min(0.9, x / vw));
    } else if (minDist === distLeft) {
      edge = 'left';
      offset = Math.max(0.1, Math.min(0.9, y / vh));
    } else {
      edge = 'right';
      offset = Math.max(0.1, Math.min(0.9, y / vh));
    }

    setPosition({ edge, offset });
  }, []);

  const handlePointerUp = useCallback(() => {
    dragRef.current = null;
    setIsDragging(false);
  }, []);

  // Keyboard shortcut handled by parent (FieldPrototype)

  const pillStyle = getToolbarStyle(position, isVertical);

  return (
    <div
      ref={toolbarRef}
      data-walkthrough="toolbar"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => { setHovered(false); setHoveredItem(null); }}
      style={{
        position: 'fixed',
        ...pillStyle,
        zIndex: 1000,
        display: 'flex',
        flexDirection: isVertical ? 'column' : 'row',
        alignItems: 'center',
        gap: 0,
        background: 'rgba(255, 255, 255, 0.92)',
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        borderRadius: isVertical ? 16 : 28,
        padding: isVertical ? '8px 6px' : '6px 8px',
        boxShadow: isDragging
          ? '0 8px 32px rgba(0,0,0,0.18), 0 0 0 2px rgba(77,196,210,0.4)'
          : '0 2px 16px rgba(0,0,0,0.08), 0 0 0 1px rgba(0,0,0,0.04)',
        transition: isDragging ? 'none' : 'box-shadow 0.2s, padding 0.3s, border-radius 0.3s',
        cursor: isDragging ? 'grabbing' : 'default',
        userSelect: 'none',
      }}
    >
      {/* Drag handle */}
      <div
        data-drag-handle="true"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: isVertical ? 24 : 8,
          height: isVertical ? 8 : 24,
          cursor: isDragging ? 'grabbing' : 'grab',
          opacity: hovered ? 0.4 : 0.15,
          transition: 'opacity 0.2s',
          flexShrink: 0,
          padding: isVertical ? '4px 0' : '0 4px',
        }}
      >
        <div style={{
          width: isVertical ? 16 : 3,
          height: isVertical ? 3 : 16,
          borderRadius: 2,
          background: '#999',
          pointerEvents: 'none',
        }} />
      </div>

      {/* Action buttons */}
      {actions.map((action) => {
        const isItemHovered = hoveredItem === action.id;
        return (
          <div
            key={action.id}
            onClick={(e) => {
              e.stopPropagation();
              onAction?.(action.id);
            }}
            onMouseEnter={() => setHoveredItem(action.id)}
            onMouseLeave={() => setHoveredItem(null)}
            style={{
              display: 'flex',
              flexDirection: isVertical ? 'row' : 'row',
              alignItems: 'center',
              gap: isItemHovered && hovered ? 6 : 0,
              padding: isVertical ? '6px 8px' : '8px 6px',
              borderRadius: 12,
              cursor: 'pointer',
              background: isItemHovered ? `${action.color}12` : 'transparent',
              transition: 'all 0.2s ease',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
            }}
          >
            <span style={{
              fontSize: 16,
              lineHeight: 1,
              flexShrink: 0,
              width: 22,
              textAlign: 'center',
              filter: isItemHovered ? 'none' : 'grayscale(30%)',
              transition: 'filter 0.2s',
            }}>
              {action.icon}
            </span>
            <span style={{
              fontFamily: '-apple-system, system-ui, sans-serif',
              fontSize: 12,
              fontWeight: 600,
              color: isItemHovered ? '#333' : '#555',
              maxWidth: isItemHovered && hovered ? 100 : 0,
              opacity: isItemHovered && hovered ? 1 : 0,
              overflow: 'hidden',
              transition: 'max-width 0.25s ease, opacity 0.2s ease, color 0.2s',
              letterSpacing: 0.2,
            }}>
              {action.label}
            </span>
          </div>
        );
      })}

      {/* Trailing drag handle */}
      <div
        data-drag-handle="true"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: isVertical ? 24 : 8,
          height: isVertical ? 8 : 24,
          cursor: isDragging ? 'grabbing' : 'grab',
          opacity: hovered ? 0.4 : 0.15,
          transition: 'opacity 0.2s',
          flexShrink: 0,
          padding: isVertical ? '4px 0' : '0 4px',
        }}
      >
        <div style={{
          width: isVertical ? 16 : 3,
          height: isVertical ? 3 : 16,
          borderRadius: 2,
          background: '#999',
          pointerEvents: 'none',
        }} />
      </div>
    </div>
  );
}
